import * as XLSX from 'xlsx';
import { PipelineCandidate } from '../services/api';

type PipelineTab = 'candidates' | 'in-review' | 'rejected';

const CANDIDATES_HEADERS = ['Talent ID', 'Name', 'Domain', 'Field', 'Batches', 'Group', 'Date Added'];
const REVIEW_HEADERS = [
  'Talent ID', 'Name', 'Domain', 'Field', 'Internal Resume Review', 'Public Source Check',
  'ID Verification', 'Overall Recommendation', 'Tags', 'Group', 'Added By', 'Added At',
];

function candidateRow(p: PipelineCandidate): Record<string, unknown> {
  return {
    'Talent ID': p.talentId,
    Name: p.name,
    Domain: p.domain,
    Field: p.field ?? '',
    Batches: (p.batches || []).join('; '),
    Group: p.uploadGroup ?? '',
    'Date Added': p.addedAt ?? '',
  };
}

function reviewCandidateRow(p: PipelineCandidate): Record<string, unknown> {
  return {
    'Talent ID': p.talentId,
    Name: p.name,
    Domain: p.domain,
    Field: p.field ?? '',
    'Internal Resume Review': p.resumeVerification ?? '',
    'Public Source Check': p.webAgentVerification ?? '',
    'ID Verification': p.linkedinVerified ?? '',
    'Overall Recommendation': p.agentRecommendation ?? '',
    Tags: (p.reviewTags || []).join('; '),
    Group: p.uploadGroup ?? '',
    'Added By': p.addedBy ?? '',
    'Added At': p.addedAt ?? '',
  };
}

/** Flattens a nested value into a spreadsheet-safe cell: primitives pass through, objects/arrays become JSON text. */
function flattenCell(value: unknown): unknown {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

function buildEnrichmentSheet(candidates: PipelineCandidate[]): XLSX.WorkSheet {
  const fieldKeys: string[] = [];
  const seen = new Set<string>();
  for (const p of candidates) {
    const enrichment = p.enrichment;
    if (enrichment && typeof enrichment === 'object') {
      for (const key of Object.keys(enrichment)) {
        if (!seen.has(key)) { seen.add(key); fieldKeys.push(key); }
      }
    }
  }

  const header = ['Talent ID', 'Domain', ...fieldKeys];
  const rows = candidates.map((p) => {
    const enrichment: Record<string, unknown> = (p.enrichment && typeof p.enrichment === 'object') ? p.enrichment : {};
    const row: Record<string, unknown> = { 'Talent ID': p.talentId, Domain: p.domain };
    for (const key of fieldKeys) row[key] = flattenCell(enrichment[key]);
    return row;
  });

  return XLSX.utils.json_to_sheet(rows, { header });
}

/** Builds a two-sheet workbook: the visible table columns (minus Actions) and a flattened Enrichment sheet keyed by Talent ID + Domain. */
export function exportPipelineCandidatesXlsx(candidates: PipelineCandidate[], tab: PipelineTab): number {
  const isCandidatesTab = tab === 'candidates';
  const headers = isCandidatesTab ? CANDIDATES_HEADERS : REVIEW_HEADERS;
  const rows = candidates.map(isCandidatesTab ? candidateRow : reviewCandidateRow);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows, { header: headers }), 'Candidates');
  XLSX.utils.book_append_sheet(wb, buildEnrichmentSheet(candidates), 'Enrichment');

  const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  XLSX.writeFile(wb, `candidates-${tab}-${ts}.xlsx`);

  return candidates.length;
}
